/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useState, useEffect, useContext } from 'react';
import { Form, FormGroup, Label, Input, InputGroup, InputGroupAddon, Button, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalFooter, ModalBody } from 'reactstrap';
import Alert from 'react-s-alert';
import Toggle from 'react-toggle';
import moment from 'moment';
import DateTimePicker from 'react-datetime-picker';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';

// service imports
import AuthService from '../../services/AuthService';
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';
import Message from '../general/Message';

const Settings = props => {

    // variables
    let timeout = 0;

    // useStates
    const [updatePasswordModal, setUpdatePasswordModal] = useState({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } });
    const [maintenanceConfirmationModal, setMaintenanceConfirmationModal] = useState({ isVisible: false });
    const [policyNConditionStatusAlertModal, setPolicyNConditionStatusAlertModal] = useState({ isVisible: false, type: "", effectiveFrom: "" });
    const [updateProvidersOnHomeModal, setUpdateProvidersOnHomeModal] = useState({ isVisible: false, data: { all: [], selected: [], pagination: { limit: 10, page: 1 } } });
    const [modalError, setModalError] = useState(null);
    const [isLoading1, setIsLoading1] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [isLoading3, setIsLoading3] = useState(false);
    const [isLoading4, setIsLoading4] = useState(false);
    const [isLoading5, setIsLoading5] = useState(false);
    const [message, setMessage] = useState({ msgBody: '', msgError: false });

    // contexts
    const { maintenance, setMaintenance } = useContext(AuthContext);
    const { theme, fontFamily, fontSize } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        if (!isLoading5) {
            setIsLoading5(true);
            AdminService.getProviders(updateProvidersOnHomeModal.data.pagination.limit, updateProvidersOnHomeModal.data.pagination.page, updateProvidersOnHomeModal.data.pagination.searchVal).then(data => {
                // console.log("data", data)
                try {
                    AdminService.getProvidersOnHome().then(data2 => {
                        // console.log("data2", data2)
                        if (data.pagination.totalPages <= data.pagination.page) {
                            data.pagination.page = data.pagination.totalPages;
                        }
                        setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, data: { all: data.providers, selected: data2.providersOnHome, pagination: data.pagination } });
                        setIsLoading5(false);
                    })
                } catch (err) {
                    Alert.error("Some error occured, please try again later or report support team.", {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    // GeneralService.logError('at Admin Providers useEffect', 'Error in try catch block', err.stack);
                    setIsLoading5(false);
                }
            });
        }
    }, [updateProvidersOnHomeModal.data.pagination.page, updateProvidersOnHomeModal.data.pagination.limit, updateProvidersOnHomeModal.data.pagination.searchVal]);

    // useEffect(() => console.log('updateProvidersOnHomeModal', updateProvidersOnHomeModal), [updateProvidersOnHomeModal])

    const handleSearch = e => {
        try {
            let value = e.target.value;
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, data: { ...updateProvidersOnHomeModal.data, pagination: { ...updateProvidersOnHomeModal.data.pagination, searchVal: value } } })
            }, 500);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            // GeneralService.logError('at Admin Providers handleSearch', 'Error in try catch block', err.stack);
        }
    }

    // handle functions
    const handleMaintenaceSubmit = () => {
        try {
            setIsLoading1(true);
            AdminService.setMaintenanceStatus(maintenance)
                .then(data => {
                    setModalError(null);
                    setMaintenanceConfirmationModal({ isVisible: false });
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading1(false);
                })
                .catch(error => {
                    setModalError(`Maintenance mode cannot be activated because there ${error.sessions === 1 ? "is" : "are"} ${error.sessions} active  ${error.sessions === 1 ? "session" : "sessions"} available between selected time range of maintenace. To activate maintenance mode make sure there are no active sessions available between before and after 2 hours of your selected time range.`);
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading1(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading1(false);
            GeneralService.logError('at Admin AdminSettings handleMaintenaceSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleProviderSelection = (item) => {
        const index = updateProvidersOnHomeModal.data.selected.findIndex(x => x._id === item._id);
        let tempArray = [...updateProvidersOnHomeModal.data.selected];
        if (index > -1) {
            tempArray.splice(index, 1);
        } else {
            tempArray.push(item);
        }
        setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, data: { ...updateProvidersOnHomeModal.data, selected: tempArray } });
    }

    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, data: { ...updateProvidersOnHomeModal.data, pagination: { ...updateProvidersOnHomeModal.data.pagination, page: index } } });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            // GeneralService.logError('at Admin Providers handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const handleProvidersOnHomeSubmit = async () => {
        try {
            setIsLoading4(true);
            const providerIds = [];
            await Promise.all(updateProvidersOnHomeModal.data.selected.map(item => {
                providerIds.push(item._id);
            }))
            AdminService.postProvidersOnHome(providerIds)
                .then(data => {
                    setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, isVisible: false });
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading4(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading4(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading4(false);
            // GeneralService.logError('at Admin AdminSettings handleMaintenaceSubmit', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmitPolicyNConditionStatus = () => {
        try {
            setIsLoading2(true);
            AdminService.setPolicyNConditionStatus(policyNConditionStatusAlertModal.type, policyNConditionStatusAlertModal.effectiveFrom)
                .then(data => {
                    setPolicyNConditionStatusAlertModal({ ...policyNConditionStatusAlertModal, isVisible: false });
                    Alert.info(data.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading2(false);
                })
                .catch(error => {
                    Alert.error(error.message.msgBody, {
                        position: 'top-right',
                        effect: 'slide'
                    });
                    setIsLoading2(false);
                });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading2(false);
            GeneralService.logError('at Admin AdminSettings handleSubmitPolicyNConditionStatus', 'Error in try catch block', err.stack);
        }
    }

    const handlePasswordSubmit = () => {
        try {
            if (updatePasswordModal.data.newPassword.length < 6) {
                setMessage({
                    msgBody: "Your password must be at least 6 characters",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[a-zA-Z]/i) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one letter",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[0-9]/) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one digit",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword.search(/[A-Z]/) < 0) {
                setMessage({
                    msgBody: "Your password must contain at least one uppercase letter",
                    msgError: true
                })
            } else if (updatePasswordModal.data.newPassword !== updatePasswordModal.data.reNewPassword) {
                setMessage({
                    msgBody: "Passwords don't match",
                    msgError: true
                })
            } else {
                setIsLoading3(true);
                AuthService.updatePassword(updatePasswordModal.data)
                    .then(data => {
                        setUpdatePasswordModal({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } });
                        Alert.info(data.message.msgBody, {
                            position: 'top-right',
                            effect: 'slide'
                        });
                        setIsLoading3(false);
                    })
                    .catch(error => {
                        setMessage(error.message);
                        setIsLoading3(false);
                    });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading3(false);
            GeneralService.logError('at Admin AdminSettings handlePasswordSubmit', 'Error in try catch block', err.stack);
        }
    }

    return (
        <div>
            <div className="p-3">
                <div>
                    <h1 style={theme.headingBold}>Settings</h1>
                    <hr style={{ borderWidth: "2px" }} />
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '15rem', fontSize: '1.2rem', fontWeight: '600' }}>Change password</label>
                    <Button type="button" className="btn ml-3" style={theme.button} onClick={() => setUpdatePasswordModal({ isVisible: true, data: { oldPassword: "", newPassword: "", reNewPassword: "" } })}>
                        Change
                    </Button>
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '15rem', fontSize: '1.2rem', fontWeight: '600' }}>Change home screen providers</label>
                    <Button type="button" className="btn ml-3" style={theme.button} onClick={() => setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, isVisible: true })}>
                        Change
                    </Button>
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '15rem', fontSize: '1.2rem', fontWeight: '600' }}>Site maintenance</label>
                    <Button type="button" className="btn ml-3" style={theme.button} onClick={() => setMaintenanceConfirmationModal({ isVisible: true })}>
                        Update
                    </Button>
                </div>

                <div className="row px-3 mt-2">
                    <label style={{ width: '15rem', fontSize: '1.2rem', fontWeight: '600' }}>Terms and Policies Update</label>
                    <Button type="button" className="btn ml-3" style={theme.button} onClick={() => setPolicyNConditionStatusAlertModal({ isVisible: true, type: "Tnc", effectiveFrom: new Date() })}>
                        Update
                    </Button>
                </div>
            </div>
            <ErrorBoundary errorAt="At Admin Settings MaintenanceConfirmationModal">
                <Modal size="lg" isOpen={maintenanceConfirmationModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setMaintenanceConfirmationModal({ isVisible: false })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Maintenance</span></ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label>Status</Label>
                                <Toggle
                                    className="ml-3"
                                    icons={false}
                                    checked={maintenance.status}
                                    onChange={(e) => setMaintenance({ ...maintenance, status: e.target.checked })}
                                />
                            </FormGroup>
                            {
                                maintenance.status ?
                                    [<FormGroup>
                                        <Label>Start time</Label>
                                        <DateTimePicker format="dd-MM-y hh:mm a" yearPlaceholder="yyyy" monthPlaceholder="mm" dayPlaceholder="dd" hourPlaceholder="hh" minutePlaceholder="mm" onChange={(value) => setMaintenance({ ...maintenance, startAt: value })} value={moment(maintenance.startAt).toDate()} className="form-control" style={theme.formInput} required={true} />
                                    </FormGroup>,
                                    <FormGroup>
                                        <Label>Duration</Label>
                                        <Input type="number" value={maintenance.duration} onChange={(e) => setMaintenance({ ...maintenance, duration: e.target.value })} />
                                    </FormGroup>]
                                    : null
                            }
                        </Form>
                        <Label>By clicking on Save button <b>Umang Wellness</b> will be <b>{maintenance.status ? "Deactivated" : "Activated"}</b> for users.</Label>
                        {modalError ? <Label style={{ color: "#f00" }}>{modalError}</Label> : null}
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleMaintenaceSubmit()} disabled={isLoading1}>
                            {isLoading1 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>

            <ErrorBoundary errorAt="At Admin Settings PolicyNConditionStatusAlertModal">
                <Modal size="lg" isOpen={policyNConditionStatusAlertModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setPolicyNConditionStatusAlertModal({ ...policyNConditionStatusAlertModal, isVisible: false })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Terms and Policies Update</span></ModalHeader>
                    <ModalBody>
                        <Form>
                            <FormGroup>
                                <Label>Select type</Label>
                                <Input type="select" id="type" name="type" onChange={(e) => setPolicyNConditionStatusAlertModal({ ...policyNConditionStatusAlertModal, type: e.target.value })} value={policyNConditionStatusAlertModal.type} required>
                                    <option value="TnC">Terms Conditions</option>
                                    <option value="PrivacyPolicy">Privacy Policy</option>
                                    <option value="CookiePolicy">Cookie Policy</option>
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Effective from</Label>
                                <DateTimePicker format="dd-MM-y" yearPlaceholder="yyyy" monthPlaceholder="mm" dayPlaceholder="dd" onChange={(value) => setPolicyNConditionStatusAlertModal({ ...policyNConditionStatusAlertModal, effectiveFrom: value })} value={moment(policyNConditionStatusAlertModal.effectiveFrom).toDate()} className="form-control" style={theme.formInput} required={true} />
                            </FormGroup>
                        </Form>
                        <Label>By clicking on Save button {policyNConditionStatusAlertModal.type} will be updated for all users.</Label>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleSubmitPolicyNConditionStatus()} disabled={isLoading2}>
                            {isLoading2 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>

            <ErrorBoundary errorAt="At Admin Settings UpdatePasswordModal">
                <Modal size="sm" isOpen={updatePasswordModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setUpdatePasswordModal({ isVisible: false, data: { oldPassword: "", newPassword: "", reNewPassword: "" } })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Change password</span></ModalHeader>
                    <ModalBody>
                        {message.msgBody ? <Message message={message} /> : null}
                        <Form>
                            <FormGroup>
                                <Label>Old password</Label>
                                <Input type="password" value={updatePasswordModal.data.oldPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, oldPassword: e.target.value } })} />
                            </FormGroup>
                            <FormGroup>
                                <Label>New password</Label>
                                <Input type="password" value={updatePasswordModal.data.newPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, newPassword: e.target.value } })} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Confirm password</Label>
                                <Input type="text" value={updatePasswordModal.data.reNewPassword} onChange={(e) => setUpdatePasswordModal({ ...updatePasswordModal, data: { ...updatePasswordModal.data, reNewPassword: e.target.value } })} />
                            </FormGroup>
                        </Form>
                        <p style={{ fontFamily: fontFamily.primary, fontSize: fontSize.sm }}>*Password must have at least 6 characters with one alphabet, one digit and one uppercase</p>
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handlePasswordSubmit()} disabled={isLoading3}>
                            {isLoading3 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Update
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>

            <ErrorBoundary errorAt="At Admin Settings UpdateProvidersOnHomeModal">
                <Modal size="lg" isOpen={updateProvidersOnHomeModal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, isVisible: false })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Update Providers On Home</span></ModalHeader>
                    <ModalBody>
                        <div>
                            <div className="card mt-4">
                                <div className="card-header">
                                    Selected Counsellors
                                </div>
                                <div className="card-body">
                                    <div className='row'>
                                        {
                                            updateProvidersOnHomeModal.data.selected.map((item, index) => (
                                                <div key={index} className="col-md-3 col-6 px-2">
                                                    <div className="card" style={{ borderRadius: "20px" }}>
                                                        {/* <img className="card-img-top" style={{ maxHeight: "20rem", width: "100%" }} src={item.profileImage} alt="Card image cap" /> */}
                                                        <div className="card-body">
                                                            <h5 className="card-title">{item.title} {item.firstName} {item.lastName}</h5>
                                                            <p className="card-text">{item.specialities.join(', ')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="card mt-4">
                                <div className="card-header">
                                    Counsellor List
                                </div>
                                <div className="card-body py-0">
                                    <div className="table-responsive">
                                        <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                            <thead>
                                                <tr>
                                                    <td colSpan="5" className="border-0 px-0">
                                                        <div className="d-flex">
                                                            <div className="ml-auto">
                                                                Search<input type="text" id="searchInput" className="ml-2" style={{ marginRight: "-20px", paddingRight: "20px" }} onChange={handleSearch}></input>
                                                                <span style={{ cursor: "pointer", marginRight: "7px", alignSelf: "center", zIndex: 100 }} onClick={() => { document.getElementById("searchInput").value = ""; setUpdateProvidersOnHomeModal({ ...updateProvidersOnHomeModal, data: { ...updateProvidersOnHomeModal.data, pagination: { ...updateProvidersOnHomeModal.data.pagination, searchVal: "" } } }); }}><i className="fa fa-times"></i></span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Total Clients</th>
                                                    <th></th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    updateProvidersOnHomeModal.data.all.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>{item.title ? item.title : ''} {item.firstName} {item.lastName}</td>
                                                            <td>{item.email}</td>
                                                            <td>{item.clientList.length}</td>
                                                            <td align="center"><a style={{ color: "#384E73" }} href="#">View details</a></td>
                                                            <td align="center"><Toggle icons={false} checked={updateProvidersOnHomeModal.data.selected.some(x => x._id === item._id)} onChange={(e) => handleProviderSelection(item, e.target.checked)} /></td>
                                                        </tr>
                                                    ))
                                                }
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td colSpan="5" className="border-0 pb-0 px-0">
                                                        <div className="d-flex">
                                                            <ErrorBoundary errorAt="At Admin Providers Pagination">
                                                                <div className="p-2">Showing {updateProvidersOnHomeModal.data.pagination.totalResults == 0 ? 0 : (1 + (10 * (updateProvidersOnHomeModal.data.pagination.page - 1)))} to {(updateProvidersOnHomeModal.data.pagination.limit + (10 * (updateProvidersOnHomeModal.data.pagination.page - 1))) > updateProvidersOnHomeModal.data.pagination.totalResults ? updateProvidersOnHomeModal.data.pagination.totalResults : (updateProvidersOnHomeModal.data.pagination.limit + (10 * (updateProvidersOnHomeModal.data.pagination.page - 1)))} of {updateProvidersOnHomeModal.data.pagination.totalResults} entries</div>
                                                                <Pagination aria-label="Page navigation example" className="ml-auto">
                                                                    <PaginationItem disabled={updateProvidersOnHomeModal.data.pagination.page == 1}>
                                                                        <PaginationLink
                                                                            onClick={e => handlePagination(e, updateProvidersOnHomeModal.data.pagination.page - 1)}
                                                                            previous
                                                                            href="#" />
                                                                    </PaginationItem>
                                                                    {[...Array(updateProvidersOnHomeModal.data.pagination.totalPages)].map((page, i) =>
                                                                        <PaginationItem active={i + 1 === updateProvidersOnHomeModal.data.pagination.page} key={i}>
                                                                            <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                                {i + 1}
                                                                            </PaginationLink>
                                                                        </PaginationItem>
                                                                    )}
                                                                    <PaginationItem disabled={updateProvidersOnHomeModal.data.pagination.page == updateProvidersOnHomeModal.data.pagination.totalPages}>
                                                                        <PaginationLink
                                                                            onClick={e => handlePagination(e, updateProvidersOnHomeModal.data.pagination.page + 1)}
                                                                            next
                                                                            href="#" />
                                                                    </PaginationItem>
                                                                </Pagination>
                                                            </ErrorBoundary>
                                                        </div>
                                                    </td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* <Label>By clicking on Save button {policyNConditionStatusAlertModal.type} will be updated for all users.</Label> */}
                    </ModalBody>
                    <ModalFooter>
                        <Button type="button" style={theme.formButton} className="btn btn-danger" onClick={() => handleProvidersOnHomeSubmit()} disabled={isLoading4}>
                            {isLoading4 ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Save
                        </Button>
                    </ModalFooter>
                </Modal>
            </ErrorBoundary>
        </div>
    )

}

export default Settings;